import { ORG_NODE_TYPE, TEAM_BOX_TYPE } from './orgChartModel';

// Kompakt karma yerleşim (spec 2026-10-24 §5.2; onaylı taslak docs/superpowers/specs/2026-10-24-organizasyon-semasi-taslak-v3.html).
// Bir kartın görünür çocukları "yaprak" (kendi görünür çocuğu yok — kapalı yönetici de) ve "açık" diye ayrılır:
//   • yaprak ≥ 3 → hepsi TEK blokta: ≤ 12 iki sütun + omurga, > 12 başlıklı ekip kutusunda dört sütun;
//   • satır = [blok] + açık çocuklar yan yana. Satır genişliği kişi sayısıyla değil açık dal sayısıyla büyür.
// Bağ çizgileri de burada hesaplanır: her kenar `data.path` (SVG d) ve `data.kids` (parçanın hizmet ettiği çocuklar) taşır.

export const CARD_SIZE = { width: 232, height: 72 };
export const GX = 20;
export const GY = 14;
export const VG = 46;
export const SP = 28;
export const LEAF_BLOCK_MIN = 3;
export const TEAM_BOX_MIN = 13;
export const TEAM_BOX_COLS = 4;
export const TEAM_BOX_PAD = 14;
export const TEAM_BOX_HEAD = 30;
export const ELBOW_R = 10;
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 0.9;
export const TOP_MARGIN = 30;
// Sayı rozeti kartın alt kenarına ortalı 22 px; çizgi rozetin altından başlar.
const PILL_HALF = 11;
const W = CARD_SIZE.width;
const H = CARD_SIZE.height;

export function blockSize(count) {
  if (count >= TEAM_BOX_MIN) {
    const rows = Math.ceil(count / TEAM_BOX_COLS);
    return {
      width: TEAM_BOX_COLS * W + (TEAM_BOX_COLS - 1) * GX + 2 * TEAM_BOX_PAD,
      height: TEAM_BOX_HEAD + rows * H + (rows - 1) * GY + TEAM_BOX_PAD,
    };
  }
  const rows = Math.ceil(count / 2);
  return { width: 2 * W + SP, height: rows * H + (rows - 1) * GY };
}

function elbow(fromX, midY, toX, endY) {
  if (Math.abs(toX - fromX) < 1) return `M${fromX},${midY} V${endY}`;
  const r = Math.min(ELBOW_R, Math.abs(toX - fromX), Math.max(0, endY - midY));
  const dir = toX < fromX ? -1 : 1;
  return `M${fromX},${midY} H${toX - dir * r} Q${toX},${midY} ${toX},${midY + r} V${endY}`;
}

/**
 * Girdi: `visibleTree` + `groupVacantSiblings` çıktısı (konumsuz düğümler, üst→çocuk kenarları). Çıktı: konumlu düğümler
 * (ekip kutuları önce — kartların altında çizilsin) ve `orgEdge` türünde, yolu hazır kenarlar.
 */
export function layoutCompactTree(nodes, edges) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const kids = new Map();
  const hasParent = new Set();
  edges.forEach((e) => {
    if (!byId.has(e.source) || !byId.has(e.target) || hasParent.has(e.target)) return;
    if (!kids.has(e.source)) kids.set(e.source, []);
    kids.get(e.source).push(e.target);
    hasParent.add(e.target);
  });
  const childrenOf = (id) => kids.get(id) || [];
  const isLeaf = (id) => childrenOf(id).length === 0;

  const groupsOf = (ids) => {
    const leaves = ids.filter(isLeaf);
    if (leaves.length >= LEAF_BLOCK_MIN) {
      return [{ block: leaves }, ...ids.filter((c) => !isLeaf(c)).map((c) => ({ id: c }))];
    }
    return ids.map((c) => ({ id: c }));
  };

  const sizes = new Map();
  const groupSize = (g) => (g.block ? blockSize(g.block.length) : sizes.get(g.id));
  const rowWidth = (groups) => groups.reduce((sum, g) => sum + groupSize(g).width, 0) + (groups.length - 1) * GX;
  const measure = (id) => {
    const kidIds = childrenOf(id);
    kidIds.forEach(measure);
    if (!kidIds.length) {
      sizes.set(id, { width: W, height: H });
      return;
    }
    const groups = groupsOf(kidIds);
    sizes.set(id, {
      width: Math.max(W, rowWidth(groups)),
      height: H + VG + Math.max(...groups.map((g) => groupSize(g).height)),
    });
  };

  const positions = new Map();
  const boxes = [];
  const outEdges = [];
  // target: React Flow 11 `onlyRenderVisibleElements` kenarı kaynak ile HEDEF düğümün kutusuna göre ayıklar — uzun bir
  // parçanın hedefi onun en uzak ucundaki kart olmalı, yoksa kaydırırken çizgi kaybolur.
  const addEdge = (source, path, kidIds, target = kidIds[0]) => {
    outEdges.push({
      id: `oe-${outEdges.length}-${source}`,
      source,
      target,
      type: 'orgEdge',
      data: { path, kids: kidIds },
    });
  };

  // spineTop: omurga / kutu girişinin başladığı y (üst yoksa çizgi yok).
  const placeBlock = (ids, x, y, parentId, spineTop) => {
    const size = blockSize(ids.length);
    const cx = x + size.width / 2;
    if (ids.length >= TEAM_BOX_MIN) {
      const parent = parentId ? byId.get(parentId)?.data : null;
      boxes.push({
        id: `team:${parentId || 'root'}`,
        type: TEAM_BOX_TYPE,
        position: { x, y },
        zIndex: -1,
        selectable: false,
        draggable: false,
        data: {
          parentId: parentId || null,
          parentName: parent ? (parent.userName || parent.titleName || null) : null,
          // Başlıktaki sayı yalnız kişi kartları: "Daha Göster" ve boş koltuk grubu kartları sayılmaz.
          count: ids.filter((id) => (byId.get(id)?.type || ORG_NODE_TYPE) === ORG_NODE_TYPE).length,
          width: size.width,
          height: size.height,
        },
      });
      ids.forEach((c, i) => {
        const col = i % TEAM_BOX_COLS;
        const row = Math.floor(i / TEAM_BOX_COLS);
        positions.set(c, { x: x + TEAM_BOX_PAD + col * (W + GX), y: y + TEAM_BOX_HEAD + row * (H + GY) });
      });
      if (parentId) addEdge(parentId, `M${cx},${spineTop} V${y}`, ids);
      return;
    }
    const rows = Math.ceil(ids.length / 2);
    ids.forEach((c, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const nx = col === 0 ? cx - SP / 2 - W : cx + SP / 2;
      const ny = y + row * (H + GY);
      positions.set(c, { x: nx, y: ny });
      if (parentId) addEdge(parentId, `M${cx},${ny + H / 2} H${col === 0 ? nx + W : nx}`, [c]);
    });
    // Omurga satır satır parçalanır: k. parça k. satırın ortasına iner ve k. ve sonraki satırların kartlarına hizmet eder —
    // vurgu yalnız seçili kartın satırına kadar uzar.
    if (parentId) {
      let from = spineTop;
      for (let row = 0; row < rows; row += 1) {
        const midY = y + row * (H + GY) + H / 2;
        const served = ids.slice(row * 2);
        addEdge(parentId, `M${cx},${from} V${midY}`, served, served[served.length - 1]);
        from = midY;
      }
    }
  };

  // Bir satırı [x, x + genişlik] aralığına ortalayarak yerleştirir; üst varsa gövde + dirsekler çizilir.
  const placeRow = (groups, x, width, y, parentId, parentCx, parentBottom) => {
    let px = x + (width - rowWidth(groups)) / 2;
    const midY = parentBottom + (y - parentBottom) / 2;
    const all = [];
    groups.forEach((g) => {
      const size = groupSize(g);
      const center = px + size.width / 2;
      if (g.block) {
        placeBlock(g.block, px, y, parentId, midY);
        all.push(...g.block);
      } else {
        place(g.id, px, y);
        all.push(g.id);
      }
      if (parentId) {
        if (g.block && g.block.length < TEAM_BOX_MIN) {
          // İki sütunlu blok: dirsek omurganın tepesinde biter, omurga midY'den aşağı iner.
          const r = Math.min(ELBOW_R, Math.abs(center - parentCx));
          const dir = center < parentCx ? -1 : 1;
          const path = Math.abs(center - parentCx) < 1
            ? `M${parentCx},${midY} V${midY}`
            : `M${parentCx},${midY} H${center - dir * r} Q${center},${midY} ${center},${midY + r}`;
          addEdge(parentId, path, g.block);
        } else {
          addEdge(parentId, elbow(parentCx, midY, center, y), g.block || [g.id]);
        }
      }
      px += size.width + GX;
    });
    if (parentId) addEdge(parentId, `M${parentCx},${parentBottom + PILL_HALF} V${midY}`, all);
  };

  const place = (id, x, y) => {
    const size = sizes.get(id);
    const cx = x + size.width / 2;
    positions.set(id, { x: cx - W / 2, y });
    const kidIds = childrenOf(id);
    if (!kidIds.length) return;
    const groups = groupsOf(kidIds);
    const childY = y + H + VG;
    if (groups.length === 1 && groups[0].block) {
      const block = blockSize(groups[0].block.length);
      placeBlock(groups[0].block, cx - block.width / 2, childY, id, y + H + PILL_HALF);
      return;
    }
    placeRow(groups, x, size.width, childY, id, cx, y + H);
  };

  const roots = nodes.filter((n) => !hasParent.has(n.id)).map((n) => n.id);
  roots.forEach(measure);
  const rootGroups = groupsOf(roots);
  if (rootGroups.length) placeRow(rootGroups, 0, rowWidth(rootGroups), 0, null, 0, 0);

  const placed = nodes
    .filter((n) => positions.has(n.id))
    .map((n) => ({ ...n, position: positions.get(n.id) }));
  return { nodes: [...boxes, ...placed], edges: outEdges };
}

// Yol vurgusu (spec 2026-10-24 §5.5): üst yolda VE parçanın çocuklarından biri yolda.
export function markHighlighted(edges, pathIds) {
  return edges.map((e) => {
    const highlight = pathIds.has(e.source) && (e.data?.kids || []).some((k) => pathIds.has(k));
    return e.data?.highlight === highlight ? e : { ...e, data: { ...e.data, highlight } };
  });
}

/**
 * Açılış kamerası (spec 2026-10-24 §5.1, taslaktaki revealCam): odak yatayda ortada, ağacın tepesi TOP_MARGIN'de;
 * yakınlaştırma kökten odağa yolu dikeyde sığdıran değer, [ZOOM_MIN, ZOOM_MAX]'a kıstırılmış. Odak yoksa ağaç ortalanır,
 * [0.6, 1] aralığında sığdırılır.
 */
export function cameraForReveal({ nodes, focusId, width, height }) {
  if (!width || !height || !nodes?.length) return null;
  const cards = nodes.filter((n) => n.type !== TEAM_BOX_TYPE);
  if (!cards.length) return null;
  const minX = Math.min(...cards.map((n) => n.position.x));
  const minY = Math.min(...cards.map((n) => n.position.y));
  const maxX = Math.max(...cards.map((n) => n.position.x + W));
  const maxY = Math.max(...cards.map((n) => n.position.y + H));
  const focus = focusId ? cards.find((n) => n.id === focusId) : null;
  if (!focus) {
    const fit = Math.min(width / (maxX - minX + 80), height / (maxY - minY + 80));
    const zoom = Math.min(1, Math.max(0.6, fit));
    return { x: width / 2 - ((minX + maxX) / 2) * zoom, y: TOP_MARGIN - minY * zoom, zoom };
  }
  const fitPath = (height - 90) / (focus.position.y - minY + 2 * H + 40);
  const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, fitPath));
  return { x: width / 2 - (focus.position.x + W / 2) * zoom, y: TOP_MARGIN - minY * zoom, zoom };
}
